import {createAction, createAsyncThunk} from '@reduxjs/toolkit'
import {RouterState} from 'redux-first-history'

import {includes, groupBy} from '@shared/helpers/collection'
import {isEmpty} from '@shared/helpers/lang'
import {sumBy} from '@shared/helpers/math'
import {mapValues} from '@shared/helpers/object'
import {qs} from '@shared/helpers/queryString'
import {SchemasOrderPurchaseConfirmation} from '@shared/schemas/order'

import {changeLocation} from '@web/actions/application'
import {
    PurchaseConfirmationErrorCode,
    PurchaseConfirmationErrorCodeType,
    ClaimStatusThrowCase,
} from '@web/core/constants'
import {ROUTER_ORDER_PURCHASE_CONFIRMATION} from '@web/core/routes'
import getApi, {createApiAsyncThunk, createWrapper} from '@web/helpers/api'
import {Sentry} from '@web/helpers/log'
import {decodeUrl} from '@web/helpers/path'
import {PurchaseConfirmationState} from '@web/slices/purchaseConfirmation/slice'
import {locationSelector} from '@web/slices/router'

import {PurchaseConfirmationResponse, PurchaseConfirmationResultResponse} from './interfaces'

const ApiBaseUrl = `/orderApi/orderSheet/purchaseConfirmation`

const api = getApi(ApiBaseUrl)

export type RootState = {
    router: RouterState
    purchaseConfirmation: PurchaseConfirmationState
}

type ThunkApi<RejectValue = unknown> = {
    state: RootState
    rejectValue: RejectValue
}

export type InitPurchaseConfirmationByAPIPayloadType = {
    data: PurchaseConfirmationResponse
    backUrl: string
    clickedId: string
}

export type InitPurchaseConfirmationResultByAPIPayloadType = {
    data: PurchaseConfirmationResultResponse
    backUrl: string
    orderNo: string
}

export const initPurchaseConfirmation = createApiAsyncThunk<
    InitPurchaseConfirmationByAPIPayloadType,
    {
        orderNo: string
        productOrderNo: string
    },
    ThunkApi<{
        title?: string
        status?: number
        detail?: string
        message?: string
        code: PurchaseConfirmationErrorCodeType
        validationFailure?: {
            message: string
            fallbackMessage: string
            reasonType: string
            reasonAttributes: object
        }
        backUrl: Maybe<string>
    }>
>('initPurchaseConfirmation', async ({orderNo, productOrderNo}, {getState, rejectWithValue}) => {
    const {query} = locationSelector(getState())

    const encodeBackUrl = query['backUrl']
    const clickedId = query['clickedId']

    const backUrl = decodeUrl(encodeBackUrl)

    const result = await createWrapper<SchemasOrderPurchaseConfirmation['/']['response']>(
        api.get('', {params: {orderNo, productOrderNo}}),
    )

    if (result.quantityClaimProgressing && includes(result.representationClaimStatusType, ClaimStatusThrowCase)) {
        return rejectWithValue({
            code: PurchaseConfirmationErrorCode.UNABLE_PURCHASE_BY_CLAIMS,
            backUrl,
        })
    }

    if (isEmpty(result.purchaseDecisionTargets)) {
        return rejectWithValue({
            code: PurchaseConfirmationErrorCode.ORDER_NOT_FOUND,
            backUrl,
        })
    }

    // 추후 타입 fix
    return {data: result as PurchaseConfirmationResponse, backUrl, clickedId}
})

export const initPurchaseConfirmationResult = createApiAsyncThunk<
    InitPurchaseConfirmationResultByAPIPayloadType,
    {
        orderNo: string
        productOrderNos: Array<string>
    },
    ThunkApi<{
        title?: string
        status?: number
        detail?: string
        message?: string
        code: PurchaseConfirmationErrorCodeType
        validationFailure?: {
            message: string
            fallbackMessage: string
            reasonType: string
            reasonAttributes: object
        }
        backUrl: Maybe<string>
    }>
>('initPurchaseConfirmationResult', async ({orderNo, productOrderNos}, {getState}) => {
    const {query} = locationSelector(getState())

    const encodeBackUrl = query['backUrl']

    const backUrl = decodeUrl(encodeBackUrl)

    const result = await createWrapper<SchemasOrderPurchaseConfirmation['/result']['response']>(
        api.get('/result', {
            params: {
                orderNo,
                productOrderNos,
            },
            paramsSerializer: (params) => {
                return qs.stringify(params)
            },
        }),
    )

    // 추후 타입 fix
    return {data: result as PurchaseConfirmationResultResponse, backUrl, orderNo}
})

export const selectAllProduct = createAction('purchaseConfirmation/selectAllProduct')

export const selectProduct = createAction<{productOrderNo: string; checked: boolean}>(
    'purchaseConfirmation/selectProduct',
)

/** 구매확정 */
export const confirmPurchase = createApiAsyncThunk<
    {result: boolean},
    {
        productOrderNos: Array<string>
    },
    ThunkApi
>('confirmPurchase', async ({productOrderNos}) => {
    const result = await createWrapper<SchemasOrderPurchaseConfirmation['/confirm']['response']>(
        api.post('/confirm', {productOrderNos}),
    )
    // 추후 타입 fix
    return result as {result: boolean}
})

/** 구매확정 완료 Action */
export const redirectToResult = createAsyncThunk<
    void,
    {orderNo: string; backUrl: string; productOrderNos: string},
    ThunkApi
>('redirectToResult', async ({orderNo, backUrl, productOrderNos}, {dispatch}) => {
    dispatch(
        changeLocation({
            type: 'PUSH',
            url: qs.stringifyUrl({
                url: ROUTER_ORDER_PURCHASE_CONFIRMATION.RESULT.getPath(`/${orderNo}`),
                query: {
                    backUrl,
                    productOrderIds: productOrderNos,
                },
            }),
        }),
    )
})

/** 리뷰대상 상품 목록 조회 */
export const getReviewEncouragementTarget = createApiAsyncThunk<
    {
        data: Maybe<{
            /** 대상 상품 주문번호 */
            productOrderNo: string
            /** 상품명 */
            productName: string
            /** 상품 이미지 URL */
            productImageUrl: Maybe<string>
            /** 예상적립금 총 합 */
            totalExpectedReviewRewardPointAmount: Maybe<number>
        }>
    },
    {productOrderNos: string[]},
    ThunkApi
>('getReviewEncouragementTargets', async ({productOrderNos}) => {
    const {reviewEncouragementTargets, reviewBenefitInfo} = await createWrapper<
        SchemasOrderPurchaseConfirmation['/reviewTargets']['response']
    >(
        api.get('/reviewTargets', {
            params: {
                productOrderNos,
            },
            paramsSerializer: (params) => {
                return qs.stringify(params)
            },
        }),
    )

    if (isEmpty(reviewEncouragementTargets)) {
        return {data: null}
    }

    /**
     * 전체 상품중 (일반리뷰 적립 max값 + 알림동의 적립값  = productReviewMaxAcmAmount)이 가장 큰 혜택정보
     * - 적립금이 같다면 상품주문번호가 큰 상품 선택
     */
    const maxBenefitInfo = reviewBenefitInfo.reduce(
        (maxBenefit, currentBenefit) => {
            const maxAmount = maxBenefit.productReviewMaxAcmAmount
            const currentAmount = currentBenefit.productReviewMaxAcmAmount

            if (currentAmount !== maxAmount) {
                return currentAmount > maxAmount ? currentBenefit : maxBenefit
            }

            return +currentBenefit.productOrderNo > +maxBenefit.productOrderNo ? currentBenefit : maxBenefit
        },
        {
            productOrderNo: '',
            productTextReviewAcmAmount: 0,
            productPhotoVideoReviewAcmAmount: 0,
            afterUseTextReviewAcmAmount: 0,
            afterUsePhotoVideoReviewAcmAmount: 0,
            customerAddReviewAcmAmount: 0,
            productReviewMaxAcmAmount: 0,
        },
    )

    /**
     * - 포인트 정보가 있다면, 리뷰적립금이 가장 큰 상품
     * - 포인트 정보가 없다면, 상품 주문번호가 가장 큰 상품
     * */
    const representativeProduct = !isEmpty(reviewBenefitInfo)
        ? reviewEncouragementTargets.find((product) => product.productOrderNo === maxBenefitInfo.productOrderNo)
        : reviewEncouragementTargets.reduce((curTarget, nextTarget) => {
              return +curTarget.productOrderNo > +nextTarget.productOrderNo ? curTarget : nextTarget
          })

    if (!representativeProduct) {
        Sentry.sendGeneralError(new Error('리뷰대상 상품 목록에 대한 혜택정보가 없음'))
    }

    return {
        data: representativeProduct
            ? {
                  productOrderNo: representativeProduct.productOrderNo,
                  productName: representativeProduct.productName,
                  productImageUrl: representativeProduct.productImageUrl,
                  totalExpectedReviewRewardPointAmount: !isEmpty(reviewBenefitInfo)
                      ? maxBenefitInfo.productReviewMaxAcmAmount
                      : 0,
              }
            : null,
    }
})

/** 구매확정 대상 상품 목록 조회 */
export const getPurchaseConfirmationTarget = createApiAsyncThunk<
    {
        data: Maybe<{
            /** 주문번호 */
            orderNo: string
            /** 상품주문번호 */
            productOrderNo: string
            /** 상품명 */
            productName: string
            /** 상품 이미지 URL */
            productImageUrl: Maybe<string>
            /** 구매확정시 예상 적립금 */
            expectedPurchaseDecisionRewardPointAmount: number
            /** 패밀리 결제 여부 */
            isFamilyPay: boolean
        }>
    },
    void,
    ThunkApi
>('getPurchaseConfirmationTargets', async () => {
    const {confirmationTargetInfo, purchaseBenefitInfo} = await createWrapper<
        SchemasOrderPurchaseConfirmation['/confirmationTargets']['response']
    >(api.get('/confirmationTargets', {}))

    // 추가 구매확정 대상이 없다면 early return
    if (isEmpty(confirmationTargetInfo)) {
        return {data: null}
    }

    // 각 상품의 적립 정보 매핑
    const items = purchaseBenefitInfo.flatMap((item) => {
        return item.acmAmount.map((amountItem) => ({
            amount: amountItem.amount,
            bundleId: amountItem.productOrderNo,
        }))
    })

    // 상품별 적립금 합계 계산
    const itemsByBundleId = groupBy('bundleId', items)
    const totalAmountByBundle = mapValues((bundledItems) => sumBy('amount', bundledItems), itemsByBundleId)

    /**
     *  적립금이 가장 큰 상품 선택
     * - 적립금이 같다면 상품주문번호가 작은 상품(가장 오래된 상품) 선택
     */
    const [maxBundleId, maxAmount] = Object.entries(totalAmountByBundle).reduce(
        ([maxBenefitBundleId, maxBenefitAmount], [currentBenefitBundleId, currentBenefitAmount]) => {
            if (currentBenefitAmount !== maxBenefitAmount) {
                return currentBenefitAmount > maxBenefitAmount
                    ? [currentBenefitBundleId, currentBenefitAmount]
                    : [maxBenefitBundleId, maxBenefitAmount]
            }

            return +maxBenefitBundleId > +currentBenefitBundleId
                ? [currentBenefitBundleId, currentBenefitAmount]
                : [maxBenefitBundleId, maxBenefitAmount]
        },
        ['', 0],
    )

    /**
     * 대표 상품 찾기
     * - 포인트 정보가 있다면, 가장 큰 포인트 정보에 부합하는 상품
     * - 포인트 정보가 없다면, 상품주문번호가 가장 작은(가장 오래된) 상품
     * */
    const representativeProduct = !isEmpty(purchaseBenefitInfo)
        ? confirmationTargetInfo.find((product) => product.productOrderNo === maxBundleId)
        : confirmationTargetInfo.reduce((curTarget, nextTarget) => {
              return +curTarget.productOrderNo > +nextTarget.productOrderNo ? nextTarget : curTarget
          })

    // 혜택정보가 존재하는데, 추가구매확정 대상에 대한 혜택 정보는 없는경우
    if (!representativeProduct) {
        Sentry.sendGeneralError(new Error('추가 구매확정 대상 상품 목록에 대한 혜택정보가 없음'))
    }

    return {
        data: representativeProduct
            ? {
                  orderNo: representativeProduct.orderNo,
                  productOrderNo: representativeProduct.productOrderNo,
                  productName: representativeProduct.productName,
                  productImageUrl: representativeProduct.productImageUrl,
                  expectedPurchaseDecisionRewardPointAmount: !isEmpty(purchaseBenefitInfo) ? maxAmount : 0,
                  isFamilyPay: representativeProduct.familyPay,
              }
            : null,
    }
})
